import axios from 'axios';
import { PATH_CUSTOMER } from '../routes/paths';

const PAYSTACK_API_URL = 'https://api.paystack.co';
const COUNTRY_CODE = 'south africa'; // Paystack uses 'south_africa' not 'ZA'

// For public operations (listing banks, initializing transactions)
// Use PUBLIC key - safe for client-side
const paystackPublicAxios = axios.create({
  baseURL: PAYSTACK_API_URL,
  headers: {
    Authorization: `Bearer ${process.env.REACT_APP_PAYSTACK_PUBLIC_KEY}`,
    'Content-Type': 'application/json',
  },
});

// For privileged operations (creating subaccounts)
// Use SECRET key - WARNING: This exposes the secret key in client-side code
// TODO: Move subaccount creation to a backend endpoint for better security
const paystackSecretAxios = axios.create({
  baseURL: PAYSTACK_API_URL,
  headers: {
    Authorization: `Bearer ${process.env.REACT_APP_PAYSTACK_SECRET_KEY}`,
    'Content-Type': 'application/json',
  },
});

export default function usePaystackService() {
  const createOrUpdateSubaccount = async (bankDetails, existingSubaccountCode = null) => {
    try {
      const payload = {
        business_name: `${bankDetails.firstName} ${bankDetails.lastName}`,
        settlement_bank: bankDetails.bankCode,
        account_number: bankDetails.accountNumber,
        // Primary contact details required for South African subaccounts
        primary_contact_name: `${bankDetails.firstName} ${bankDetails.lastName}`,
        primary_contact_email: bankDetails.email,
        // Set percentage_charge to 0 - we'll use transaction_charge for precise control per transaction
        // percentage_charge: 0 means subaccount gets 0% by default, then transaction_charge overrides per transaction
        percentage_charge: 0,
        currency: 'ZAR',
      };

      let response;
      if (existingSubaccountCode) {
        // Update existing subaccount - requires SECRET key
        response = await paystackSecretAxios.put(`/subaccount/${existingSubaccountCode}`, payload);
      } else {
        // Create new subaccount - requires SECRET key
        response = await paystackSecretAxios.post('/subaccount', payload);
      }
      return response.data.data;
    } catch (error) {
      console.error('Error with Paystack subaccount:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  };

  const listBanks = async () => {
    try {
      // List banks - uses PUBLIC key (safe for client-side)
      const response = await paystackPublicAxios.get(`/bank?country=${COUNTRY_CODE}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching South African banks list:', error?.response?.data || error);
      throw error;
    }
  };

  const verifyAccount = async (accountNumber, bankCode) => {
    try {
      // Verify account - requires SECRET key for South African accounts
      const response = await paystackSecretAxios.get(
        `/bank/resolve?account_number=${accountNumber}&bank_code=${bankCode}&country=${COUNTRY_CODE}`
      );
      return response.data.data;
    } catch (error) {
      console.error('Error verifying account:', error);
      // Don't throw - account verification is optional, allow form submission to continue
      return null;
    }
  };

  const initializeTransaction = async ({
    amount,
    email,
    subaccountCode,
    bookingId,
    quoteId,
    paymentType,
    providerAmount,
    metadata = {},
  }) => {
    try {
      const { origin } = window.location;

      // Add payment type and remaining payment flag directly to the callback URL
      const paymentTypeValue = paymentType || 'deposit';
      const isRemainingPayment = paymentTypeValue === 'remaining';
      const callbackPath = PATH_CUSTOMER.bookings.paymentComplete(bookingId);
      const callbackUrl = `${origin}${callbackPath}?payment_type=${paymentTypeValue}&is_remaining_payment=${isRemainingPayment}`;

      console.log('Setting up transaction with callback URL:', callbackUrl);
      console.log('Payment details:', { paymentType, isRemainingPayment, amount });

      // Log transaction details for debugging
      console.log('Initializing transaction with:', { amount, email, subaccountCode, bookingId });

      // Build the transaction payload
      const transactionPayload = {
        amount: Math.round(amount * 100), // Convert to cents and round to ensure integer
        email,
        currency: 'ZAR', // Change to ZAR layer
        callback_url: callbackUrl,
        metadata: {
          booking_id: bookingId,
          quote_id: quoteId,
          payment_type: paymentType || 'full',
          is_remaining_payment: isRemainingPayment,
          provider_subaccount: subaccountCode,
          ...metadata,
          custom_fields: [
            {
              display_name: 'Booking ID',
              variable_name: 'booking_id',
              value: bookingId,
            },
            {
              display_name: 'Payment Type',
              variable_name: 'payment_type',
              value: paymentType || 'full',
            },
            {
              display_name: 'Is Remaining Payment',
              variable_name: 'is_remaining_payment',
              value: isRemainingPayment ? 'true' : 'false',
            },
          ],
        },
      };

      // Add subaccount for payment splitting if subaccountCode is provided
      // IMPORTANT: transaction_charge is the flat fee the MAIN ACCOUNT (Stintz) receives
      // The subaccount (provider) receives: total - transaction_charge - paystack_fees
      if (subaccountCode && providerAmount) {
        transactionPayload.subaccount = subaccountCode;
        // Calculate platform amount (what Stintz should receive)
        const platformAmount = amount - providerAmount;
        // transaction_charge is what the MAIN account (Stintz) gets in kobo/cents
        transactionPayload.transaction_charge = Math.round(platformAmount * 100);
        console.log('Payment split enabled:', {
          subaccount: subaccountCode,
          totalAmount: amount,
          providerAmount,
          platformAmount,
          transaction_charge: transactionPayload.transaction_charge,
        });
      } else if (subaccountCode && !providerAmount) {
        console.warn('Subaccount provided but no providerAmount specified - payment will not be split correctly');
      } else {
        console.warn('No subaccount provided - payment will not be split');
      }

      // Initialize transaction
      // Use SECRET key if subaccount is involved (split payments require elevated permissions)
      // Use PUBLIC key for simple transactions without subaccounts
      const axiosInstance = subaccountCode ? paystackSecretAxios : paystackPublicAxios;
      const response = await axiosInstance.post('/transaction/initialize', transactionPayload);

      console.log('Transaction initialization response:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('Error initializing transaction:', error?.response?.data || error);
      throw error;
    }
  };

  /**
   * Fetch details of a subscription
   * @param {string} subscriptionCode - The subscription ID or code to fetch
   * @returns {Promise<Object>} - The subscription details
   */
  const manageSubscription = async (subscriptionCode) => {
    try {
      if (!subscriptionCode) {
        throw new Error('Subscription code is required');
      }

      // Create a secure axios instance with the secret key for server-side operations
      // Note: This should ideally be done through a backend API endpoint for security
      const generateLinkResponse = await axios({
        method: 'GET',
        url: `${PAYSTACK_API_URL}/subscription/${subscriptionCode}/manage/link`,
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_PAYSTACK_SECRET_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      window.open(generateLinkResponse?.data?.data?.link, '_blank');
    } catch (error) {
      console.error('Error fetching subscription:', error?.response?.data || error);
      throw error;
    }
  };

  return {
    createOrUpdateSubaccount,
    listBanks,
    verifyAccount,
    initializeTransaction,
    manageSubscription,
  };
}
